/**
 * RustDesk widgety. Stránka je jen ModuleWorkspace — zámek rozvržení a
 * „Výchozí“ drží ModulePanel, ne tahle vrstva.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import i18n from '../../../lib/i18n';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import {
  Alert,
  AsyncStatusToggle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  Input,
  Label,
  Modal,
  RefreshButton,
  SearchInput,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../../../components/ui';
import { Fail2banReactionToggle } from '../../../components/ui/Fail2banReactionToggle';
import {
  BookUser,
  Copy,
  ExternalLink,
  Globe,
  KeyRound,
  Monitor,
  Plus,
  Server,
  Shield,
  Terminal,
  Trash2,
} from 'lucide-react';

interface Station {
  id: number;
  hostname: string;
  rustdesk_id: string | null;
  alias: string | null;
  os: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  last_seen: string | null;
  online: boolean;
  source: 'acm' | 'both' | 'cortendesk';
  cd_id: number | null;
  cd_status: 'active' | 'pending' | 'disabled' | null;
  cd_last_online_at: string | null;
  cd_version: string | null;
  cd_platform: string | null;
  cd_user: string | null;
  cd_disabled: boolean;
  console_user?: string | null;
  console_name?: string | null;
  console_title?: string | null;
  technician_login?: string | null;
  technician_name?: string | null;
  technician_title?: string | null;
  technician_group?: string | null;
}

interface ConsoleSession {
  computer: string;
  user: string;
  since: string;
  source: 'station' | 'dc';
}

interface TechGroup {
  source: string;
  name: string;
}

interface AdSource {
  id: string;
  label: string;
  kind: string;
  available?: boolean;
}

interface DirectoryPerson {
  login: string;
  display_name: string;
  job_title?: string;
  group?: string;
}

interface TenantRow {
  id: string;
  name: string;
}

interface ListResp {
  ok: boolean;
  stations: Station[];
  cortendesk?: { ok: boolean; error?: string };
}

interface ServerInfo {
  ok: boolean;
  public_host: string;
  public_api?: string;
  hostname_prefix?: string;
  base_domain?: string;
  fqdn?: string;
  desired_host?: string;
  host_mode?: 'acm' | 'custom';
  published?: boolean;
  id_port: number;
  relay_port: number;
  rendezvous_server: string;
  configured: boolean;
  udp_note?: string;
  server_status?: string;
  enroll_status?: string;
  aliases?: string[];
}

interface AccessState {
  ok?: boolean;
  error?: string;
  warning?: string;
  published?: boolean;
  hostname_prefix?: string;
  base_domain?: string;
  fqdn?: string;
  desired_fqdn?: string;
  host_mode?: 'acm' | 'custom';
  public_custom_host?: string;
  public_url?: string;
  aliases?: string[];
  sso_enabled?: boolean;
  sso_registered?: boolean;
  local_login?: boolean;
  oidc_enabled?: boolean;
  console_auth?: boolean;
}

type AuditKind = 'conn' | 'file' | 'alarm' | 'console';
type AuditRow = Record<string, unknown>;
type Book = Record<string, unknown> & {
  id: number;
  name: string;
  is_personal?: boolean;
  owner?: { id: number; username: string } | null;
};

const CORTENDESK_SOURCE = 'https://github.com/marcpope/cortendesk';

function scopeKey(scope: EffectiveScope): string {
  return `${scope.node ?? ''}:${scope.identity ?? ''}`;
}

function call<T>(tool: string, args: Record<string, unknown>, scope: EffectiveScope): Promise<T> {
  return callWidgetTool<T>(tool, args, scope, { silent: true });
}

function copy(text: string, label = i18n.t('rustdesk.copied')) {
  navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() =>
    toast.error(i18n.t('rustdesk.copyFailed'))
  );
}

function fmtTime(v: unknown): string {
  if (typeof v !== 'string' || !v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

function str(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'string' ? v : String(v);
}

function loginKey(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const slash = raw.includes('\\') ? raw.slice(raw.lastIndexOf('\\') + 1) : raw;
  return (slash.split('@')[0] ?? '').replace(/\$$/, '').trim().toLowerCase();
}

function hostKey(value: string): string {
  return value.trim().toLowerCase().replace(/\$$/, '').split('.')[0] ?? '';
}

function useServer(scope: EffectiveScope) {
  return useQuery({
    queryKey: ['rustdesk-server', scopeKey(scope)],
    queryFn: () => call<ServerInfo>('rustdesk.server_info', {}, scope),
    refetchInterval: 10_000,
  });
}

function useAccess(scope: EffectiveScope) {
  return useQuery({
    queryKey: ['rustdesk-access', scopeKey(scope)],
    queryFn: () => call<AccessState>('rustdesk.access', {}, scope),
  });
}

export function RustdeskStationsWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [installCmd, setInstallCmd] = useState<string | null>(null);
  const [groupPick, setGroupPick] = useState('');

  const tenantsQ = useQuery({
    queryKey: ['rustdesk-tenants', scopeKey(scope)],
    queryFn: () => call<{ ok: boolean; tenants: TenantRow[] }>('mesh.tenants', { action: 'list' }, scope),
  });
  const stationsQ = useQuery({
    queryKey: ['rustdesk-stations', scopeKey(scope)],
    queryFn: () => call<ListResp>('rustdesk.list', {}, scope),
    refetchInterval: 30_000,
  });
  const serverQ = useServer(scope);

  const tenants = tenantsQ.data?.tenants ?? [];
  const allStations = stationsQ.data?.stations ?? [];
  const cortendesk = stationsQ.data?.cortendesk;
  const consoleUrl = serverQ.data?.public_api ||
    (serverQ.data?.public_host ? `https://${serverQ.data.public_host}` : '');
  const selectedTenant = tenants.find((tn) => tn.id === filter) ?? null;

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of allStations) {
      if (!s.tenant_id) continue;
      m[s.tenant_id] = (m[s.tenant_id] ?? 0) + 1;
    }
    return m;
  }, [allStations]);
  const unassignedCount = allStations.filter((s) => !s.tenant_id).length;

  const stations = useMemo(() => {
    let list = allStations;
    if (filter === 'unassigned') list = list.filter((s) => !s.tenant_id);
    else if (filter !== 'all') list = list.filter((s) => s.tenant_id === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) =>
        s.hostname.toLowerCase().includes(q) || (s.rustdesk_id ?? '').includes(q) ||
        (s.alias ?? '').toLowerCase().includes(q) || (s.cd_user ?? '').toLowerCase().includes(q) ||
        (s.console_name ?? '').toLowerCase().includes(q) || (s.console_user ?? '').includes(q) ||
        (s.technician_name ?? '').toLowerCase().includes(q) || (s.technician_title ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allStations, filter, search]);

  const invalidateStations = () => void qc.invalidateQueries({ queryKey: ['rustdesk-stations'] });
  const tenantId = filter !== 'all' && filter !== 'unassigned' ? filter : null;

  const del = useMutation({
    mutationFn: (id: number) => call('rustdesk.delete', { id }, scope),
    onSuccess: () => {
      toast.success(t('rustdesk.stationRemoved'));
      invalidateStations();
    },
    onError: () => toast.error(t('rustdesk.removeFailed')),
  });

  const deviceSet = useMutation({
    mutationFn: (p: { action: 'enable' | 'disable'; s: Station }) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.device_set', {
        action: p.action,
        ...(p.s.cd_id != null ? { cd_id: p.s.cd_id } : { rustdesk_id: p.s.rustdesk_id }),
      }, scope),
    onSuccess: (r, p) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('rustdesk.deviceSetFailed'));
        return;
      }
      toast.success(p.action === 'disable' ? t('rustdesk.deviceDisabled') : t('rustdesk.deviceEnabled'));
      invalidateStations();
    },
    onError: () => toast.error(t('rustdesk.deviceSetFailed')),
  });

  const connectMut = useMutation({
    mutationFn: async (p: { s: Station; mode: 'native' | 'web' }) => {
      const r = await call<{ ok: boolean; error?: string; deep_link?: string; webclient_url?: string | null }>(
        'rustdesk.connect',
        { id: p.s.rustdesk_id ?? String(p.s.id), with_secret: p.mode === 'native' },
        scope,
      );
      return { r, mode: p.mode };
    },
    onSuccess: ({ r, mode }) => {
      if (!r.ok) {
        toast.error(r.error ?? t('rustdesk.noRustdeskId'));
        return;
      }
      const url = mode === 'web' ? r.webclient_url : r.deep_link;
      if (!url) {
        toast.error(t('rustdesk.noRustdeskId'));
        return;
      }
      globalThis.open(url, '_blank');
    },
    onError: () => toast.error(t('rustdesk.noRustdeskId')),
  });

  const genToken = useMutation({
    mutationFn: () =>
      call<{ ok: boolean; install_command: string }>('rustdesk.enroll_token', {
        tenant_id: tenantId ?? undefined,
      }, scope),
    onSuccess: (r) => setInstallCmd(r.install_command),
    onError: () => toast.error(t('rustdesk.tokenCreateFailed')),
  });

  const sessionsQ = useQuery({
    queryKey: ['rustdesk-sessions', scopeKey(scope)],
    queryFn: async () => {
      try {
        const data = await call<{ ok?: boolean; sessions?: ConsoleSession[] }>('fleet.sessions', {}, scope);
        return data.sessions ?? [];
      } catch {
        return [] as ConsoleSession[];
      }
    },
    refetchInterval: 60_000,
  });
  const staffQ = useQuery({
    queryKey: ['rustdesk-staff', scopeKey(scope)],
    queryFn: () => call<{ ok?: boolean; groups?: TechGroup[] }>('rustdesk.staff', { action: 'get' }, scope),
  });
  const sourcesQ = useQuery({
    queryKey: ['rustdesk-ad-sources', scopeKey(scope)],
    queryFn: async () => {
      try {
        const data = await call<{ ok?: boolean; sources?: AdSource[] }>('ad.users', { action: 'sources' }, scope);
        return (data.sources ?? []).filter((s) => s.available && s.kind === 'ad');
      } catch {
        return [] as AdSource[];
      }
    },
  });
  const adSources = sourcesQ.data ?? [];
  const bound = staffQ.data?.groups ?? [];
  const directoryQ = useQuery({
    queryKey: ['rustdesk-ad-groups', scopeKey(scope), adSources.map((s) => s.id).join(',')],
    enabled: adSources.length > 0,
    queryFn: async () => {
      const rows: Array<TechGroup & { label: string }> = [];
      for (const source of adSources) {
        try {
          const data = await call<{ ok?: boolean; groups?: Array<{ name?: string }> }>('ad.users', {
            action: 'groups',
            source: source.id,
            limit: 500,
          }, scope);
          for (const group of data.groups ?? []) {
            if (!group.name) continue;
            rows.push({
              source: source.id,
              name: group.name,
              label: adSources.length > 1 ? `${group.name} · ${source.label}` : group.name,
            });
          }
        } catch {
          /* další doména */
        }
      }
      return rows;
    },
  });
  const membersQ = useQuery({
    queryKey: ['rustdesk-techs', scopeKey(scope), bound.map((g) => `${g.source}:${g.name}`).join('|')],
    enabled: bound.length > 0,
    queryFn: async () => {
      const people: DirectoryPerson[] = [];
      const seen = new Set<string>();
      for (const group of bound) {
        try {
          const data = await call<{ ok?: boolean; users?: DirectoryPerson[] }>('ad.users', {
            action: 'list',
            source: group.source || undefined,
            group: group.name,
            limit: 200,
          }, scope);
          for (const user of data.users ?? []) {
            const key = loginKey(user.login || '');
            if (!key || seen.has(key)) continue;
            seen.add(key);
            people.push({ ...user, login: key, group: group.name });
          }
        } catch {
          /* skupina bez výpisu */
        }
      }
      return people;
    },
  });
  const sessionsByHost = useMemo(() => {
    const map = new Map<string, ConsoleSession>();
    for (const session of sessionsQ.data ?? []) {
      const key = hostKey(session.computer);
      if (key && !map.has(key)) map.set(key, session);
    }
    return map;
  }, [sessionsQ.data]);
  const sessionLogins = useMemo(() => {
    const keys = new Set<string>();
    for (const session of sessionsQ.data ?? []) {
      const key = loginKey(session.user);
      if (key) keys.add(key);
    }
    return [...keys].sort().slice(0, 40);
  }, [sessionsQ.data]);
  const memberLogins = useMemo(
    () => new Set((membersQ.data ?? []).map((p) => p.login)),
    [membersQ.data],
  );
  const missingLogins = sessionLogins.filter((key) => !memberLogins.has(key));
  const lookupQ = useQuery({
    queryKey: ['rustdesk-logins', scopeKey(scope), adSources.map((s) => s.id).join(','), missingLogins.join(',')],
    enabled: missingLogins.length > 0 && adSources.length > 0,
    queryFn: async () => {
      const out: DirectoryPerson[] = [];
      for (const login of missingLogins) {
        for (const source of adSources) {
          try {
            const data = await call<{ ok?: boolean; user?: DirectoryPerson }>('ad.users', {
              action: 'get',
              source: source.id,
              id: login,
            }, scope);
            if (data.user?.login) {
              out.push({ ...data.user, login: loginKey(data.user.login) });
              break;
            }
          } catch {
            /* účet v téhle doméně není */
          }
        }
      }
      return out;
    },
  });
  const people = useMemo(() => {
    const map = new Map<string, DirectoryPerson>();
    for (const person of membersQ.data ?? []) map.set(person.login, person);
    for (const person of lookupQ.data ?? []) if (!map.has(person.login)) map.set(person.login, person);
    return map;
  }, [membersQ.data, lookupQ.data]);
  const positionCounts = useMemo(() => {
    const totals = new Map<string, number>();
    for (const station of allStations) {
      const key = (station.technician_title || station.technician_name || station.technician_login || '').trim();
      if (!key) continue;
      totals.set(key, (totals.get(key) ?? 0) + 1);
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'cs'));
  }, [allStations]);
  const noted = useRef(new Set<string>());
  useEffect(() => {
    const jobs: Array<{ id: number; login: string; name: string; title: string; stamp: string }> = [];
    for (const station of allStations) {
      if (station.id <= 0) continue;
      const session = sessionsByHost.get(hostKey(station.hostname));
      if (!session) continue;
      const login = loginKey(session.user);
      if (!login) continue;
      if (lookupQ.isFetching && missingLogins.includes(login)) continue;
      const person = people.get(login);
      const title = person?.job_title ?? (station.console_user === login ? (station.console_title ?? '') : '');
      const name = person?.display_name || session.user;
      if (station.console_user === login && (station.console_title ?? '') === title) continue;
      const stamp = `${station.id}:${login}:${title}`;
      if (noted.current.has(stamp)) continue;
      noted.current.add(stamp);
      jobs.push({ id: station.id, login, name, title, stamp });
    }
    if (!jobs.length) return;
    void Promise.all(jobs.map((job) =>
      call<{ ok?: boolean }>('rustdesk.staff', {
        action: 'assign',
        role: 'user',
        id: job.id,
        login: job.login,
        name: job.name,
        title: job.title,
      }, scope).then((r) => {
        if (r && r.ok === false) noted.current.delete(job.stamp);
      }).catch(() => {
        noted.current.delete(job.stamp);
      })
    )).then(() => invalidateStations());
  }, [allStations, sessionsByHost, people, lookupQ.isFetching, missingLogins, scope]);

  const saveGroups = useMutation({
    mutationFn: (groups: TechGroup[]) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.staff', { action: 'set_groups', groups }, scope),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('rustdesk.staffFailed'));
        return;
      }
      setGroupPick('');
      void qc.invalidateQueries({ queryKey: ['rustdesk-staff'] });
    },
    onError: () => toast.error(t('rustdesk.staffFailed')),
  });
  const assignTech = useMutation({
    mutationFn: (p: { id: number; login: string; name: string; title: string; group: string }) =>
      call<{ ok?: boolean; error?: string }>('rustdesk.staff', { action: 'assign', role: 'technician', ...p }, scope),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('rustdesk.staffFailed'));
        return;
      }
      invalidateStations();
    },
    onError: () => toast.error(t('rustdesk.staffFailed')),
  });

  const filterBtn = (id: string, label: string, count: number) => (
    <button
      type='button'
      key={id}
      className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center justify-between gap-2 ${
        filter === id ? 'bg-gray-800/50' : 'hover:bg-gray-800/30'
      }`}
      onClick={() => setFilter(id)}
    >
      <span className='truncate'>{label}</span>
      <span className='text-xs text-gray-500 tabular-nums'>{count}</span>
    </button>
  );

  return (
    <div className='space-y-3 p-1'>
      <StatsGrid>
        <StatCard label={t('rustdesk.statStations')} value={allStations.length} icon={<Monitor size={18} />} />
        <StatCard
          label={t('rustdesk.statOnline')}
          value={allStations.filter((s) => s.online).length}
          icon={<StatusDot status='online' />}
        />
        <StatCard label={t('rustdesk.statTenants')} value={tenants.length} icon={<Server size={18} />} />
        <StatCard
          label={t('rustdesk.statServer')}
          value={serverQ.data?.configured ? 'OK' : t('rustdesk.configMissing')}
          icon={<Globe size={18} />}
        />
      </StatsGrid>
      {cortendesk && !cortendesk.ok && (
        <Alert variant='warning'>
          {t('rustdesk.cortendeskDown', { error: cortendesk.error ?? '' })}
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>{t('rustdesk.technicianGroup', 'Technici')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-xs text-gray-500'>{t('rustdesk.technicianGroupEmpty')}</p>
          <div className='flex flex-wrap items-center gap-2'>
            {bound.map((group) => (
              <Button
                key={`${group.source}:${group.name}`}
                size='sm'
                variant='secondary'
                disabled={saveGroups.isPending}
                onClick={() => saveGroups.mutate(bound.filter((item) =>
                  !(item.source === group.source && item.name === group.name)
                ))}
              >
                {group.name} · {t('rustdesk.aliasRemove', 'Odebrat')}
              </Button>
            ))}
            <Select
              size='sm'
              value={groupPick}
              placeholder={t('rustdesk.technicianGroupPick', 'Vyber skupinu')}
              onChange={(event) => setGroupPick(event.target.value)}
              options={(directoryQ.data ?? [])
                .filter((group) => !bound.some((item) =>
                  item.source === group.source && item.name.toLowerCase() === group.name.toLowerCase()
                ))
                .map((group) => ({ value: `${group.source}||${group.name}`, label: group.label }))}
            />
            <Button
              size='sm'
              disabled={!groupPick || saveGroups.isPending}
              onClick={() => {
                const split = groupPick.indexOf('||');
                if (split < 0) return;
                saveGroups.mutate([
                  ...bound,
                  { source: groupPick.slice(0, split), name: groupPick.slice(split + 2) },
                ]);
              }}
            >
              {t('rustdesk.technicianGroupAdd', 'Navázat')}
            </Button>
          </div>
          {positionCounts.length > 0 && (
            <div className='flex flex-wrap gap-2'>
              {positionCounts.map(([title, count]) => (
                <Badge key={title} variant='info'>{t('rustdesk.positionCount', { title, count })}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <div className='grid gap-4 lg:grid-cols-[240px_1fr]'>
        <Card>
          <CardHeader>
            <CardTitle className='text-sm'>{t('rustdesk.stationFilter', 'Stanice')}</CardTitle>
          </CardHeader>
          <CardContent className='p-2 space-y-0.5'>
            {filterBtn('all', t('rustdesk.filterAll', 'Všechny'), allStations.length)}
            {filterBtn('unassigned', t('rustdesk.filterUnassigned', 'Nezařazené'), unassignedCount)}
            {tenants.map((tn) => filterBtn(tn.id, tn.name, counts[tn.id] ?? 0))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between gap-2'>
            <CardTitle className='text-sm'>
              {filter === 'unassigned'
                ? t('rustdesk.filterUnassigned', 'Nezařazené')
                : selectedTenant
                ? t('rustdesk.stationsTenantTitle', { name: selectedTenant.name })
                : t('rustdesk.stationsAllTitle')}
            </CardTitle>
            <div className='flex items-center gap-2'>
              <Button size='sm' variant='secondary' onClick={() => genToken.mutate()}>
                <Terminal size={14} /> {t('rustdesk.installCommand')}
              </Button>
              <Button size='sm' onClick={() => setAddOpen(true)}>
                <Plus size={14} /> {t('rustdesk.addStation')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            <SearchInput value={search} onChange={setSearch} placeholder={t('rustdesk.searchPlaceholder')} />
            {stations.length === 0
              ? (
                <EmptyState
                  icon={<Monitor size={32} />}
                  title={t('rustdesk.noStations')}
                  description={t('rustdesk.noStationsDesc')}
                />
              )
              : (
                <div className='divide-y'>
                  {stations.map((s) => (
                    <div key={`${s.source}-${s.id}`} className='flex items-center gap-3 py-2'>
                      <StatusDot status={s.online ? 'online' : 'offline'} />
                      <div className='min-w-0 flex-1'>
                        <div className='font-medium truncate flex items-center gap-2'>
                          <span className='truncate'>
                            {s.hostname}
                            {s.alias ? <span className='text-gray-500 font-normal'> · {s.alias}</span> : null}
                          </span>
                          {!s.tenant_id && (
                            <span title={t('rustdesk.unassignedHint')}>
                              <Badge variant='info'>{t('rustdesk.unassigned')}</Badge>
                            </span>
                          )}
                          {s.cd_disabled && <Badge variant='error'>{t('rustdesk.stationDisabledBadge')}</Badge>}
                          {s.cd_status === 'pending' && <Badge variant='warning'>pending</Badge>}
                        </div>
                        <div className='text-xs text-gray-500 truncate'>
                          ID: <code>{s.rustdesk_id ?? t('rustdesk.notInstalled')}</code>
                          {s.tenant_name ? ` · ${s.tenant_name}` : ''}
                          {s.cd_platform || s.os ? ` · ${s.cd_platform ?? s.os}` : ''}
                          {s.cd_version ? ` · ${t('rustdesk.versionLabel')} ${s.cd_version}` : ''}
                          {s.cd_user ? ` · ${t('rustdesk.ownerLabel')} ${s.cd_user}` : ''}
                          {!s.online && (s.cd_last_online_at || s.last_seen)
                            ? ` · ${t('rustdesk.lastOnline')} ${fmtTime(s.cd_last_online_at ?? s.last_seen)}`
                            : ''}
                        </div>
                        {(() => {
                          const session = sessionsByHost.get(hostKey(s.hostname));
                          const live = session ? loginKey(session.user) : '';
                          const login = live || s.console_user || '';
                          const person = login ? people.get(loginKey(login)) : undefined;
                          const signedName = person?.display_name || (live ? session?.user : s.console_name) || login;
                          const signedTitle = person?.job_title ||
                            (login && login === (s.console_user ?? '') ? (s.console_title ?? '') : '');
                          const techs = membersQ.data ?? [];
                          const techOptions = [
                            { value: '', label: t('rustdesk.technicianNone', 'bez technika') },
                            ...techs.map((tech) => ({
                              value: tech.login,
                              label: tech.job_title ? `${tech.display_name} · ${tech.job_title}` : tech.display_name,
                            })),
                          ];
                          if (
                            s.technician_login &&
                            !techOptions.some((opt) => opt.value === s.technician_login)
                          ) {
                            techOptions.push({
                              value: s.technician_login,
                              label: s.technician_title
                                ? `${s.technician_name || s.technician_login} · ${s.technician_title}`
                                : (s.technician_name || s.technician_login),
                            });
                          }
                          const liveTech = live ? techs.find((tech) => tech.login === live) : undefined;
                          return (
                            <div className='mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500'>
                              <span>
                                {t('rustdesk.signedIn', 'Přihlášený')}: {signedName || t('rustdesk.signedInEmpty', 'nikdo')}
                                {signedTitle ? ` · ${t('rustdesk.position', 'Pozice')} ${signedTitle}` : ''}
                                {session?.source === 'dc' ? ` · ${t('rustdesk.fromDc', 'z řadiče')}` : ''}
                              </span>
                              {s.id > 0 && (
                                <span className='flex items-center gap-1'>
                                  {t('rustdesk.technician', 'Technik')}
                                  <Select
                                    size='sm'
                                    value={s.technician_login ?? ''}
                                    placeholder={t('rustdesk.technicianNone', 'bez technika')}
                                    disabled={assignTech.isPending || (bound.length === 0 && !s.technician_login)}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      const tech = techs.find((item) => item.login === value);
                                      assignTech.mutate({
                                        id: s.id,
                                        login: value,
                                        name: tech?.display_name || '',
                                        title: tech?.job_title || '',
                                        group: tech?.group || '',
                                      });
                                    }}
                                    options={techOptions}
                                  />
                                </span>
                              )}
                              {s.id > 0 && liveTech && s.technician_login !== live && (
                                <Button
                                  size='sm'
                                  variant='ghost'
                                  disabled={assignTech.isPending}
                                  onClick={() => assignTech.mutate({
                                    id: s.id,
                                    login: liveTech.login,
                                    name: liveTech.display_name,
                                    title: liveTech.job_title || '',
                                    group: liveTech.group || '',
                                  })}
                                >
                                  {t('rustdesk.technicianAssign', 'Přiřadit jako technika')}
                                </Button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className='flex flex-wrap items-center gap-1'>
                        <Button
                          size='sm'
                          variant='ghost'
                          disabled={!s.rustdesk_id || connectMut.isPending}
                          onClick={() => connectMut.mutate({ s, mode: 'native' })}
                        >
                          <Monitor size={14} /> {t('rustdesk.connect')}
                        </Button>
                        <Button
                          size='sm'
                          variant='ghost'
                          disabled={!s.rustdesk_id || !consoleUrl || connectMut.isPending}
                          title={t('rustdesk.openInBrowserHint')}
                          onClick={() => connectMut.mutate({ s, mode: 'web' })}
                        >
                          <ExternalLink size={14} /> {t('rustdesk.openInBrowser')}
                        </Button>
                        {s.cd_id != null && (
                          <Button
                            size='sm'
                            variant='ghost'
                            disabled={deviceSet.isPending}
                            onClick={async () => {
                              const action = s.cd_disabled ? 'enable' : 'disable';
                              if (
                                action === 'enable' ||
                                await themedConfirm(t('rustdesk.confirmDisableStation', { hostname: s.hostname }))
                              ) deviceSet.mutate({ action, s });
                            }}
                          >
                            {s.cd_disabled ? t('rustdesk.enableStation') : t('rustdesk.disableStation')}
                          </Button>
                        )}
                        {s.source !== 'cortendesk' && (
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={async () => {
                              if (await themedConfirm(t('rustdesk.confirmRemoveStation', { hostname: s.hostname }))) {
                                del.mutate(s.id);
                              }
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      </div>
      {addOpen && (
        <AddStationModal scope={scope} tenantId={tenantId} onClose={() => setAddOpen(false)} />
      )}
      {installCmd && (
        <Modal open onClose={() => setInstallCmd(null)} title={t('rustdesk.installCommand')}>
          <div className='space-y-3'>
            <p className='text-sm text-gray-600'>
              {t('rustdesk.installHintRun')} <b>{t('rustdesk.installHintAdmin')}</b>. {t('rustdesk.installHintBody')}
              {selectedTenant ? t('rustdesk.installHintTenant', { name: selectedTenant.name }) : ''}.
            </p>
            <div className='flex items-start gap-2'>
              <code className='block flex-1 bg-gray-900 text-gray-100 p-3 rounded text-xs break-all'>
                {installCmd}
              </code>
              <Button size='sm' variant='secondary' onClick={() => copy(installCmd, t('rustdesk.commandCopied'))}>
                <Copy size={14} />
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function RustdeskBooksWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const [bookId, setBookId] = useState<number | null>(null);
  const booksQ = useQuery({
    queryKey: ['rustdesk-books', scopeKey(scope)],
    queryFn: () => call<{ ok: boolean; error?: string; books?: Book[] }>('rustdesk.address_books', {}, scope),
  });
  const peersQ = useQuery({
    queryKey: ['rustdesk-book-peers', scopeKey(scope), bookId],
    queryFn: () =>
      call<{ ok: boolean; error?: string; peers?: Record<string, unknown>[] }>('rustdesk.address_books', {
        book_id: bookId,
      }, scope),
    enabled: bookId != null,
  });
  const books = booksQ.data?.books ?? [];
  const peers = peersQ.data?.peers ?? [];
  const selected = books.find((b) => b.id === bookId) ?? null;

  return (
    <div className='grid gap-4 lg:grid-cols-[320px_1fr] p-1'>
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>{t('rustdesk.booksTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='p-2'>
          {booksQ.data?.ok === false && <Alert variant='warning'>{booksQ.data.error}</Alert>}
          {books.length === 0
            ? <EmptyState icon={<BookUser size={28} />} title={t('rustdesk.booksEmpty')} />
            : (
              <div className='divide-y'>
                {books.map((b) => (
                  <button
                    type='button'
                    key={b.id}
                    className={`w-full text-left py-2 px-2 text-sm flex items-center justify-between gap-2 ${
                      b.id === bookId ? 'bg-gray-800/40 rounded' : ''
                    }`}
                    onClick={() => setBookId(b.id)}
                  >
                    <span className='truncate'>{b.name}</span>
                    <Badge variant={b.is_personal ? 'default' : 'info'}>
                      {b.is_personal ? t('rustdesk.bookPersonal') : t('rustdesk.bookShared')}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>
            {selected
              ? t('rustdesk.peersTitle', { name: selected.name, owner: selected.owner?.username ?? '—' })
              : t('rustdesk.tabBooks')}
          </CardTitle>
        </CardHeader>
        <CardContent className='p-2'>
          {!selected
            ? <EmptyState icon={<BookUser size={28} />} title={t('rustdesk.peersEmpty')} />
            : (
              <DataTable
                compact
                data={peers}
                rowKey={(p, i) => String(p.id ?? p.rustdesk_id ?? i)}
                emptyMessage={t('rustdesk.peersEmpty')}
                columns={[
                  { key: 'rustdesk_id', label: 'ID', render: (p) => <code>{str(p.rustdesk_id)}</code> },
                  { key: 'alias', label: t('rustdesk.colAlias'), render: (p) => str(p.alias) },
                  { key: 'hostname', label: t('rustdesk.hostnameLabel'), render: (p) => str(p.hostname) },
                  { key: 'platform', label: t('rustdesk.colPlatform'), render: (p) => str(p.platform) },
                  {
                    key: 'tags',
                    label: t('rustdesk.colTags'),
                    render: (p) =>
                      Array.isArray(p.tags)
                        ? (p.tags as Array<string | { name?: string }>).map((x) =>
                          typeof x === 'string' ? x : x?.name ?? ''
                        ).filter(Boolean).join(', ') || '—'
                        : '—',
                  },
                ]}
              />
            )}
        </CardContent>
      </Card>
    </div>
  );
}

export function RustdeskAuditWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<AuditKind>('conn');
  const [idFilter, setIdFilter] = useState('');
  const q = useQuery({
    queryKey: ['rustdesk-audit', scopeKey(scope), kind, idFilter],
    queryFn: () =>
      call<{ ok: boolean; error?: string; entries?: AuditRow[] }>('rustdesk.audit', {
        kind,
        id: idFilter.trim() || undefined,
        limit: 200,
      }, scope),
    refetchInterval: 60_000,
  });
  const rows = q.data?.entries ?? [];
  const columns = useMemo(() => {
    const time = { key: 'created_at', label: t('rustdesk.colTime'), render: (r: AuditRow) => fmtTime(r.created_at) };
    const station = {
      key: 'rustdesk_id',
      label: t('rustdesk.colStation'),
      render: (r: AuditRow) => <code>{str(r.rustdesk_id)}</code>,
    };
    const from = {
      key: 'from_peer',
      label: t('rustdesk.colFrom'),
      render: (r: AuditRow) => `${str(r.from_name)}${r.from_peer ? ` (${String(r.from_peer)})` : ''}`,
    };
    switch (kind) {
      case 'file':
        return [
          time,
          station,
          from,
          { key: 'direction', label: t('rustdesk.colDirection'), render: (r: AuditRow) => str(r.direction) },
          { key: 'path', label: t('rustdesk.colPath'), render: (r: AuditRow) => str(r.path) },
          { key: 'ip', label: t('rustdesk.colIp'), render: (r: AuditRow) => str(r.ip) },
        ];
      case 'alarm':
        return [
          time,
          station,
          { key: 'type_label', label: t('rustdesk.colType'), render: (r: AuditRow) => str(r.type_label ?? r.typ) },
          { key: 'info', label: t('rustdesk.colInfo'), render: (r: AuditRow) => str(r.info) },
        ];
      case 'console':
        return [
          time,
          { key: 'username', label: t('rustdesk.colOperator'), render: (r: AuditRow) => str(r.username) },
          { key: 'action', label: t('rustdesk.colAction'), render: (r: AuditRow) => str(r.action) },
          { key: 'ip', label: t('rustdesk.colIp'), render: (r: AuditRow) => str(r.ip) },
        ];
      default:
        return [
          time,
          station,
          from,
          { key: 'action', label: t('rustdesk.colAction'), render: (r: AuditRow) => str(r.action) },
          { key: 'ip', label: t('rustdesk.colIp'), render: (r: AuditRow) => str(r.ip) },
          { key: 'closed_at', label: t('rustdesk.colClosed'), render: (r: AuditRow) => fmtTime(r.closed_at) },
          { key: 'note', label: t('rustdesk.colNote'), render: (r: AuditRow) => str(r.note) },
        ];
    }
  }, [kind, t]);

  return (
    <Card className='m-1'>
      <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-2'>
        <CardTitle className='text-sm'>{t('rustdesk.tabAudit')}</CardTitle>
        <div className='flex items-center gap-2'>
          <Select size='sm' value={kind} onChange={(e) => setKind(e.target.value as AuditKind)}>
            <option value='conn'>{t('rustdesk.auditKindConn')}</option>
            <option value='file'>{t('rustdesk.auditKindFile')}</option>
            <option value='alarm'>{t('rustdesk.auditKindAlarm')}</option>
            <option value='console'>{t('rustdesk.auditKindConsole')}</option>
          </Select>
          <SearchInput value={idFilter} onChange={setIdFilter} placeholder={t('rustdesk.auditFilterId')} />
          <RefreshButton onRefresh={() => void q.refetch()} loading={q.isFetching} />
        </div>
      </CardHeader>
      <CardContent className='p-2'>
        {q.data?.ok === false && <Alert variant='warning'>{q.data.error}</Alert>}
        <DataTable
          compact
          data={rows}
          rowKey={(r, i) => String(r.id ?? i)}
          emptyMessage={t('rustdesk.auditEmpty')}
          columns={columns}
        />
      </CardContent>
    </Card>
  );
}

function statusLabel(status?: string): string {
  if (status === 'running') return i18n.t('rustdesk.serverRunning', 'běží');
  if (status === 'missing') return i18n.t('rustdesk.serverMissing', 'není');
  if (!status || status === 'exited' || status === 'dead') return i18n.t('rustdesk.serverStopped', 'zastaveno');
  return status;
}

export function RustdeskEngineWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const serverQ = useServer(scope);
  const server = serverQ.data;
  const running = server?.server_status === 'running';
  const consoleUrl = server?.public_api || (server?.public_host ? `https://${server.public_host}` : '');

  const ctl = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart') =>
      call<{ ok?: boolean; error?: string; api_error?: string }>('rustdesk.server_start', { action }, scope),
    onSuccess: (r, action) => {
      if (r && r.ok === false) toast.error(r.error ?? t('rustdesk.serverStartFailed'));
      else if (r?.api_error) toast.warning(r.api_error);
      else if (action === 'stop') toast.success(t('rustdesk.serverStopped', 'zastaveno'));
      else toast.success(t('rustdesk.serverStarted'));
      void qc.invalidateQueries({ queryKey: ['rustdesk-server'] });
      void qc.invalidateQueries({ queryKey: ['rustdesk-stations'] });
    },
    onError: () => toast.error(t('rustdesk.serverStartFailed')),
  });

  return (
    <Card className='m-1'>
      <CardHeader>
        <CardTitle className='text-sm'>{t('rustdesk.serverLabel')}</CardTitle>
      </CardHeader>
      <CardContent className='py-3 text-sm space-y-3'>
        <div className='flex flex-wrap items-center gap-3'>
          <Badge variant={running ? 'success' : 'warning'}>
            {statusLabel(server?.server_status)}
          </Badge>
          {server?.enroll_status && server.enroll_status !== server.server_status && (
            <span className='text-xs text-gray-500'>
              enroll: {statusLabel(server.enroll_status)}
            </span>
          )}
          <span>
            {t('rustdesk.rendezvousLabel')} <code>{server?.rendezvous_server || '—'}</code>
          </span>
          <span>
            {t('rustdesk.relayPortLabel')} <code>{server?.relay_port ?? '—'}</code>
          </span>
          <Badge variant={server?.configured ? 'success' : 'warning'}>
            {server?.configured ? t('rustdesk.configured') : t('rustdesk.configIncomplete')}
          </Badge>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {running
            ? (
              <>
                <Button
                  size='sm'
                  variant='secondary'
                  loading={ctl.isPending && ctl.variables === 'stop'}
                  disabled={ctl.isPending}
                  onClick={() => ctl.mutate('stop')}
                >
                  {t('rustdesk.stopServer', 'Zastavit')}
                </Button>
                <Button
                  size='sm'
                  variant='secondary'
                  loading={ctl.isPending && ctl.variables === 'restart'}
                  disabled={ctl.isPending}
                  onClick={() => ctl.mutate('restart')}
                >
                  {t('rustdesk.restartServer', 'Restart')}
                </Button>
              </>
            )
            : (
              <Button
                size='sm'
                variant='secondary'
                loading={ctl.isPending && ctl.variables === 'start'}
                disabled={ctl.isPending}
                onClick={() => ctl.mutate('start')}
              >
                <Server size={14} /> {t('rustdesk.startServer')}
              </Button>
            )}
          <Button
            size='sm'
            variant='secondary'
            disabled={!consoleUrl}
            title={t('rustdesk.consoleHint')}
            onClick={() => consoleUrl && globalThis.open(consoleUrl, '_blank')}
          >
            <ExternalLink size={14} /> {t('rustdesk.consoleOpen')}
          </Button>
        </div>
        <div className='space-y-1'>
          <p className='text-xs font-medium text-gray-400'>{t('rustdesk.modesTitle')}</p>
          <p className='text-[11px] text-gray-500'>
            {t('rustdesk.modeWs')}{' '}
            <code>{server?.public_host ? `wss://${server.public_host}/ws/id` : '—'}</code>
          </p>
          <p className='text-[11px] text-gray-500'>
            {t('rustdesk.modeFixed')}{' '}
            <code>{t('rustdesk.modeFixedPorts', { host: server?.public_host || '—' })}</code>
          </p>
          <p className='text-[11px] text-gray-500'>{server?.udp_note ?? t('rustdesk.udpNote')}</p>
        </div>
        <p className='text-[11px] text-gray-500'>
          {t('rustdesk.sourceNotice')}{' '}
          <a className='underline' href={CORTENDESK_SOURCE} target='_blank' rel='noreferrer'>
            {CORTENDESK_SOURCE}
          </a>
        </p>
      </CardContent>
    </Card>
  );
}

export function RustdeskPublicWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const serverQ = useServer(scope);
  const accessQ = useAccess(scope);
  const server = serverQ.data;
  const access = accessQ.data;
  const [mode, setMode] = useState<'acm' | 'custom' | null>(null);
  const [prefixDraft, setPrefixDraft] = useState<string | null>(null);
  const [customDraft, setCustomDraft] = useState<string | null>(null);
  const [aliasDraft, setAliasDraft] = useState('');
  const effective = mode ?? (access?.host_mode === 'custom' ? 'custom' : 'acm');
  const base = access?.base_domain || server?.base_domain || '';
  const aliases = access?.aliases ?? server?.aliases ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['rustdesk-server'] });
    void qc.invalidateQueries({ queryKey: ['rustdesk-access'] });
  };
  const accessCall = async (body: Record<string, unknown>) => {
    const r = await call<AccessState>('rustdesk.access', body, scope);
    if (!r || r.ok === false) throw new Error(r?.error ?? t('rustdesk.accessFailed'));
    if (r.warning) toast.warning(r.warning);
    return r;
  };
  const applyUrl = useMutation({
    mutationFn: () => {
      if (effective === 'custom') {
        const host = (customDraft ?? access?.public_custom_host ?? '').trim();
        if (!host) throw new Error(t('rustdesk.customHostPlaceholder'));
        return accessCall({ public_host: host });
      }
      const prefix = (prefixDraft ?? access?.hostname_prefix ?? server?.hostname_prefix ?? 'rustdeskhub').trim() ||
        'rustdeskhub';
      return accessCall({ hostname_prefix: prefix, host_mode: 'acm' });
    },
    onSuccess: (r) => {
      toast.success(t('rustdesk.urlApplied', { host: r.desired_fqdn || r.fqdn || r.public_url || '' }));
      setPrefixDraft(null);
      setCustomDraft(null);
      setMode(null);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t('rustdesk.accessFailed')),
  });
  const aliasMut = useMutation({
    mutationFn: (body: { alias_add?: string; alias_remove?: string }) => accessCall(body),
    onSuccess: () => {
      setAliasDraft('');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t('rustdesk.accessFailed')),
  });

  return (
    <Card className='m-1'>
      <CardHeader>
        <CardTitle className='text-sm'>{t('rustdesk.publicTitle', 'Veřejná adresa')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3 text-sm'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge variant={server?.published || access?.published ? 'success' : 'default'}>
            {server?.published || access?.published
              ? t('rustdesk.publishedBadge')
              : t('rustdesk.notPublished')}
          </Badge>
          {(access?.public_url || server?.public_host) && (
            <code className='text-xs'>{access?.public_url || server?.public_host}</code>
          )}
        </div>
        <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
          <AsyncStatusToggle
            value={Boolean(access?.published ?? server?.published)}
            pending={applyUrl.isPending}
            onToggle={async (v) => {
              await accessCall({ enabled: v });
              invalidate();
            }}
            onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
            renderIcon={(on) => <Globe size={12} className={on ? 'text-emerald-400' : 'text-gray-600'} />}
            label={
              <div className='flex flex-col'>
                <span className='text-[11px] text-gray-300'>{t('rustdesk.publicInterface')}</span>
                <span className='text-[10px] text-gray-600'>{t('rustdesk.publicInterfaceHint')}</span>
              </div>
            }
          />
        </div>
        <div className='flex gap-2'>
          <Button
            size='sm'
            variant={effective === 'acm' ? 'primary' : 'secondary'}
            onClick={() => setMode('acm')}
          >
            {t('rustdesk.hostModeAcm', 'Adresa ACM')}
          </Button>
          <Button
            size='sm'
            variant={effective === 'custom' ? 'primary' : 'secondary'}
            onClick={() => setMode('custom')}
          >
            {t('rustdesk.hostModeCustom', 'Vlastní adresa')}
          </Button>
        </div>
        {effective === 'acm'
          ? (
            <div className='flex flex-wrap items-end gap-2'>
              <div>
                <Label>{t('rustdesk.hostnamePrefix')}</Label>
                <div className='flex items-center gap-1 mt-1'>
                  <Input
                    size='sm'
                    className='w-40'
                    value={prefixDraft ?? access?.hostname_prefix ?? server?.hostname_prefix ?? 'rustdeskhub'}
                    onChange={(e) => setPrefixDraft(e.target.value)}
                  />
                  <span className='text-xs text-gray-500'>
                    {base ? `.${base}` : t('rustdesk.noBaseDomain')}
                  </span>
                </div>
              </div>
              <Button size='sm' variant='secondary' loading={applyUrl.isPending} onClick={() => applyUrl.mutate()}>
                {t('rustdesk.applyUrl')}
              </Button>
            </div>
          )
          : (
            <div className='flex flex-wrap items-end gap-2'>
              <div>
                <Label>{t('rustdesk.customHost', 'Veřejná adresa')}</Label>
                <Input
                  size='sm'
                  className='w-72 mt-1'
                  placeholder={t('rustdesk.customHostPlaceholder', 'např. rust.example.com')}
                  value={customDraft ?? access?.public_custom_host ?? ''}
                  onChange={(e) => setCustomDraft(e.target.value)}
                />
              </div>
              <Button size='sm' variant='secondary' loading={applyUrl.isPending} onClick={() => applyUrl.mutate()}>
                {t('rustdesk.applyUrl')}
              </Button>
            </div>
          )}
        <div className='space-y-2'>
          <p className='text-xs font-medium text-gray-400'>{t('rustdesk.aliasesTitle', 'Aliasy')}</p>
          {aliases.length === 0
            ? <p className='text-xs text-gray-500'>{t('rustdesk.aliasEmpty', 'Žádný alias')}</p>
            : aliases.map((alias) => (
              <div key={alias} className='flex items-center justify-between gap-2'>
                <code className='text-xs truncate'>{alias}</code>
                <Button
                  size='sm'
                  variant='ghost'
                  disabled={aliasMut.isPending}
                  onClick={() => aliasMut.mutate({ alias_remove: alias })}
                >
                  {t('rustdesk.aliasRemove', 'Odebrat')}
                </Button>
              </div>
            ))}
          <div className='flex flex-wrap items-center gap-2'>
            <Input
              size='sm'
              className='w-72'
              placeholder={t('rustdesk.aliasPlaceholder', 'např. rd.example.com')}
              value={aliasDraft}
              onChange={(e) => setAliasDraft(e.target.value)}
            />
            <Button
              size='sm'
              variant='secondary'
              loading={aliasMut.isPending}
              disabled={!aliasDraft.trim()}
              onClick={() => aliasMut.mutate({ alias_add: aliasDraft.trim() })}
            >
              {t('rustdesk.aliasAdd', 'Přidat alias')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function RustdeskLoginWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const accessQ = useAccess(scope);
  const access = accessQ.data;
  const published = Boolean(access?.published);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['rustdesk-access'] });
    void qc.invalidateQueries({ queryKey: ['rustdesk-server'] });
  };
  const accessCall = async (body: Record<string, unknown>) => {
    const r = await call<AccessState>('rustdesk.access', body, scope);
    if (!r || r.ok === false) throw new Error(r?.error ?? t('rustdesk.accessFailed'));
    if (r.warning) toast.warning(r.warning);
    return r;
  };

  return (
    <Card className='m-1'>
      <CardHeader>
        <CardTitle className='text-sm'>{t('rustdesk.loginTitle', 'Přihlášení')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        {published && (
          <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
            <AsyncStatusToggle
              value={Boolean(access?.console_auth ? access.oidc_enabled : access?.sso_enabled)}
              actual={Boolean(access?.sso_registered)}
              onToggle={async (v) => {
                await accessCall({ sso: v });
                invalidate();
              }}
              onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
              renderIcon={(on) => <Shield size={12} className={on ? 'text-blue-400' : 'text-gray-600'} />}
              label={
                <div className='flex flex-col'>
                  <span className='text-[11px] text-gray-300'>{t('rustdesk.ssoToggle')}</span>
                  <span className='text-[10px] text-gray-600'>{t('rustdesk.ssoHint')}</span>
                </div>
              }
            />
          </div>
        )}
        <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
          <AsyncStatusToggle
            value={access?.console_auth === false ? false : (access?.local_login ?? true)}
            disabled={!access || access.console_auth === false}
            onToggle={async (v) => {
              await accessCall({ local_login: v });
              invalidate();
            }}
            onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
            renderIcon={(on) => <KeyRound size={12} className={on ? 'text-amber-400' : 'text-gray-600'} />}
            label={
              <div className='flex flex-col'>
                <span className='text-[11px] text-gray-300'>{t('rustdesk.localLogin')}</span>
                <span className='text-[10px] text-gray-600'>
                  {access?.console_auth === false ? t('rustdesk.localLoginUnknown') : t('rustdesk.localLoginHint')}
                </span>
              </div>
            }
          />
        </div>
        <Fail2banReactionToggle variant='publish-row' />
      </CardContent>
    </Card>
  );
}

export function RustdeskFleetWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState('');
  const tenantsQ = useQuery({
    queryKey: ['rustdesk-tenants', scopeKey(scope)],
    queryFn: () => call<{ tenants: TenantRow[] }>('mesh.tenants', { action: 'list' }, scope),
  });
  const agentsQ = useQuery({
    queryKey: ['fleet-agents-rustdesk', scopeKey(scope)],
    queryFn: () =>
      call<
        { agents?: Array<{ agent_id?: string; hostname?: string; os?: string; status?: string }> } | Array<
          { agent_id?: string; hostname?: string; os?: string; status?: string }
        >
      >('fleet.agent_list', {}, scope),
    staleTime: 15_000,
  });
  const raw = agentsQ.data;
  const agents = Array.isArray(raw) ? raw : (raw?.agents ?? []);
  const deployMut = useMutation({
    mutationFn: (agent_ids?: string[]) =>
      call<{ ok?: boolean; error?: string; queued?: string[] }>('rustdesk.fleet_deploy', {
        tenant_id: tenantId || undefined,
        ...(agent_ids?.length ? { agent_ids } : {}),
      }, scope),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('rustdesk.fleetDeployFailed'));
        return;
      }
      toast.success(t('rustdesk.fleetDeployQueued', { count: r.queued?.length ?? 0 }));
      void qc.invalidateQueries({ queryKey: ['rustdesk-stations'] });
    },
    onError: () => toast.error(t('rustdesk.fleetDeployFailed')),
  });

  return (
    <Card className='m-1'>
      <CardHeader>
        <CardTitle className='text-sm'>{t('rustdesk.fleetTitle')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-xs text-gray-500'>{t('rustdesk.fleetHint')}</p>
        <div className='flex flex-wrap items-end justify-between gap-2'>
          <div>
            <Label>{t('rustdesk.fleetTenant', 'Registrovat pod')}</Label>
            <Select className='mt-1' size='sm' value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value=''>{t('rustdesk.fleetNoTenant', 'bez tenanta')}</option>
              {(tenantsQ.data?.tenants ?? []).map((tn) => (
                <option key={tn.id} value={tn.id}>{tn.name}</option>
              ))}
            </Select>
          </div>
          <Button size='sm' onClick={() => deployMut.mutate(undefined)} loading={deployMut.isPending}>
            {t('rustdesk.deployAll')}
          </Button>
        </div>
        {agents.length === 0
          ? <div className='text-xs text-gray-500'>{t('rustdesk.noFleetAgents')}</div>
          : (
            <div className='divide-y'>
              {agents.map((a) => (
                <div key={a.agent_id ?? a.hostname} className='flex items-center justify-between gap-2 py-2 text-sm'>
                  <span className='truncate'>
                    {a.hostname || a.agent_id}
                    <span className='text-[11px] text-gray-500 ml-2'>{a.status} {a.os}</span>
                  </span>
                  <Button
                    size='sm'
                    variant='secondary'
                    disabled={!a.agent_id || deployMut.isPending}
                    onClick={() => a.agent_id && deployMut.mutate([a.agent_id])}
                  >
                    {t('rustdesk.deployOne')}
                  </Button>
                </div>
              ))}
            </div>
          )}
      </CardContent>
    </Card>
  );
}

function AddStationModal(
  { scope, tenantId, onClose }: { scope: EffectiveScope; tenantId: string | null; onClose: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hostname, setHostname] = useState('');
  const [alias, setAlias] = useState('');
  const [os, setOs] = useState('windows');
  const create = useMutation({
    mutationFn: () =>
      call('rustdesk.create', {
        hostname: hostname.trim(),
        alias: alias.trim() || undefined,
        os,
        tenant_id: tenantId ?? undefined,
      }, scope),
    onSuccess: () => {
      toast.success(t('rustdesk.stationAdded'));
      void qc.invalidateQueries({ queryKey: ['rustdesk-stations'] });
      onClose();
    },
    onError: () => toast.error(t('rustdesk.addFailed')),
  });

  return (
    <Modal open onClose={onClose} title={t('rustdesk.addStation')}>
      <div className='space-y-3'>
        <div>
          <Label>{t('rustdesk.hostnameLabel')}</Label>
          <Input
            className='w-full mt-1'
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder={t('rustdesk.hostnamePlaceholder')}
          />
        </div>
        <div>
          <Label>{t('rustdesk.aliasOptionalLabel')}</Label>
          <Input
            className='w-full mt-1'
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder={t('rustdesk.aliasPlaceholder')}
          />
        </div>
        <div>
          <Label>{t('rustdesk.osLabel')}</Label>
          <Select className='w-full mt-1' value={os} onChange={(e) => setOs(e.target.value)}>
            <option value='windows'>Windows</option>
            <option value='linux'>Linux</option>
            <option value='macos'>macOS</option>
          </Select>
        </div>
        <p className='text-xs text-gray-500'>{t('rustdesk.preregisterHint')}</p>
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!hostname.trim() || create.isPending} onClick={() => create.mutate()}>
            {t('rustdesk.add')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
